import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-[transform,background-color,color,border-color,box-shadow] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-background)',
    'disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-(--color-primary) text-(--color-primary-foreground) hover:brightness-110 shadow-sm',
        secondary:
          'bg-(--color-secondary) text-(--color-secondary-foreground) hover:bg-(--color-muted)',
        outline:
          'border border-(--color-border) bg-(--color-background) text-(--color-foreground) hover:bg-(--color-muted)',
        ghost: 'text-(--color-foreground) hover:bg-(--color-muted)',
        destructive:
          'bg-(--color-destructive) text-(--color-destructive-foreground) hover:brightness-110 shadow-sm',
        link: 'text-(--color-primary) underline-offset-4 hover:underline',
        gradient:
          'bg-gradient-to-br from-(--color-brand-from) via-(--color-brand-mid) to-(--color-brand-to) text-white shadow-[var(--shadow-glow)] hover:brightness-110',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        default: 'h-10 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
        xl: 'h-12 px-7 text-base',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), className);

  // Radix Slot requires exactly one child. When asChild is set, forward the
  // caller's element verbatim and skip the loading spinner branch.
  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" /> : null}
      {children}
    </button>
  );
});

export { buttonVariants };
